import type {KnowledgeArticleSimpleResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {SidebarLinkKnowledgeBase} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetKnowledgeArticleListV2Query} from 'src/redux/queries/knowledgeBase';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {KnowledgeBaseDocumentTitle} from 'src/pages/Modules/KnowledgeBase/shared/knowledgeArticleHelpers';
import {useKnowledgeArticleColumns} from 'src/pages/Modules/KnowledgeBase/shared/useKnowledgeArticleColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const KnowledgeArticleListPage = () => {
  useSetDocumentTitle(KnowledgeBaseDocumentTitle);
  const navigate = useAppNavigate();

  const columns = useKnowledgeArticleColumns();

  const tableState = useTableState({
    identifier: 'knowledge-article-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'updatedAt', desc: true}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetKnowledgeArticleListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleAddNew = () => navigate(PathUtility.KnowledgeBaseCreate());
  const handleView = (row: KnowledgeArticleSimpleResponse) =>
    navigate(PathUtility.KnowledgeBaseView(row.id));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => handleView(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.KnowledgeBase}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action variant="create" onClick={handleAddNew}>
                New Article
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.KnowledgeBase}>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkKnowledgeBase.icon}
          subtitle="Author and manage knowledge-base articles">
          {KnowledgeBaseDocumentTitle}
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.KnowledgeBase(),
  element: <KnowledgeArticleListPage />,
  module: ModuleEnum.KnowledgeBase,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
